import React from 'react';

/**
 * MetaThread — the cell meta-thread timeline.
 *
 * Renders items in chronological order with a vertical connector line and
 * a colored dot per item kind.  Loaded on-demand: the parent triggers a
 * listMetaThread call; this component is purely presentational.
 *
 * Props:
 *   items    — thread item array (from client.listMetaThread); may be null/[]
 *   loading  — boolean
 */

/** Map an item kind string to a CSS class used for the dot colour. */
function dotClass(kind) {
  if (!kind) return 'mc-mt-dot';
  const k = kind.toLowerCase();
  if (k === 'ok' || k === 'success' || k === 'completed') return 'mc-mt-dot ok';
  if (k === 'warn' || k === 'warning' || k === 'input_needed') return 'mc-mt-dot warn';
  if (k === 'step' || k === 'progress') return 'mc-mt-dot step';
  if (k === 'sys' || k === 'system') return 'mc-mt-dot sys';
  if (k === 'error' || k === 'failed') return 'mc-mt-dot error';
  return 'mc-mt-dot';
}

/**
 * Format a timestamp for display.  Falls back gracefully to raw string if
 * the value is not parseable.
 */
function formatTime(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return ts;
  }
}

function MetaThread({ items, loading }) {
  if (loading) {
    return <div className="text-muted">Loading thread…</div>;
  }

  if (!items || items.length === 0) {
    return <div className="text-muted">No thread items yet.</div>;
  }

  return (
    <div className="mc-mt">
      {items.map((item, idx) => {
        // Real thread items: content is [{type:"text", text}] array; kind lives in item.metadata.type.
        const rawContent = Array.isArray(item.content) ? item.content : [];
        const body = rawContent
          .filter(c => c && c.type === 'text' && c.text)
          .map(c => c.text)
          .join('\n') || '';
        const kind = ((item.metadata && item.metadata.type) || '').toLowerCase();
        const who = item.user_id || kind || 'system';
        const ts = item.created_at || '';

        return (
          <div key={item.item_id || idx} className="mc-mt-ev">
            <div className={dotClass(kind)} />
            <div className="mc-mt-body">
              <div className="mc-mt-who">
                {who}
                {ts && <span className="mc-mt-ts">{formatTime(ts)}</span>}
              </div>
              {body && <div className="mc-mt-text">{body}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default MetaThread;
